import { Breadcrumbs, Card, Container, createStyles, Grid, makeStyles, Theme, Typography } from '@material-ui/core';
import HomeIcon from '@mui/icons-material/Home';
import { Skeleton } from '@mui/material';
import {
	Downloads,
	Hourly,
	MostViewed,
	PageViews,
	Traffic,
	TrafficDaily,
	UserCard,
	ViewsBy,
	ButtonClicks,
	ScanByDate,
	ScanByCode,
} from '../components/Dashboard/Analytics/Charts';
import { ViiisionDateRangePicker } from '../components/Dashboard/Analytics/ViiisionDateRangePicker';
import { AnalyticsTip } from '../components/Tips';
import { useChangeRoute } from '../hooks/useChangeRoute';
import { useHydrateStore } from '../hooks/useHydrateStore';
import { useAppSelector } from '../store/hooks';
import {
	_downloadsData,
	_hourly,
	_isLoading,
	_languagesAccessed,
	// _newTraffic,
	_pageViews,
	// _returningTraffic,
	_sessionData,
	_topContent,
	_trafficDaily,
	_userTraffic,
	_viewsBy,
	_botRenderedShares,
	_buttonClicks,
	_qrCodeScans,
} from '../store/slices/analyticsSlice';
import { _topics } from '../store/slices/topicSlice';
import { Keyframes } from '../theme/Keyframes';
import { colors } from '../theme/palette';
import { LanguagesAccessedCard } from '../components/Dashboard/Analytics/Charts/LanguagesAccessedCard';
import { TopicViewsCount } from '../components/Dashboard/Analytics/Charts/PageViewsCount';
import { BotRenderedShares } from '../components/Dashboard/Analytics/Charts/BotRenderedShares';
import { ButtonClicksTable } from '../components/Dashboard/Analytics/Charts/ButtonClicksTable';

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		containerWidth: {
			width: '100%',
			margin: 30,
		},
		dashboardTitle: {
			fontFamily: 'Open Sans',
			fontWeight: 400,
			fontSize: '24px',
			lineHeight: '32px',
			letterSpacing: '0.18px',
			color: '#49525D',
			padding: 3,
			marginBottom: 10,
		},
		fade: {
			animation: 'fade 1s ease .5s both',
			// width: 300,
			// height: '550px',
		},
		breadcrumbLink: {
			display: 'flex',
			alignItems: 'center',
			cursor: 'pointer',
			color: colors.tealAccent,
			textDecoration: 'none',
			'&:hover': {
				textDecoration: 'underline',
			},
			'& .MuiSvgIcon-root': {
				marginRight: 5,
			},
		},
		dialog: {
			'& .MuiDialog-container': {
				height: 'auto',
				width: 'auto',
			},
		},
		card: {
			padding: 20,
			margin: '40px 20px 20px 20px',
			border: '1px solid #e0e0e0',
			borderRadius: 6,
			animation: 'fade 1s ease .5s both',
			height: 'auto',
		},
	})
);

export type AnalyticsProps = {};

export const Analytics = (props: AnalyticsProps) => {
	const classes = useStyles();
	const toDashboard = useChangeRoute('dashboard');
	const topics = useAppSelector(_topics);
	useHydrateStore(); //this will only hydrate if we access this page with an empty store
	const isLoading = useAppSelector(_isLoading);

	//DATA
	const pageViews = useAppSelector(_pageViews);
	const userTraffic = useAppSelector(_userTraffic);
	// const newTraffic = useAppSelector(_newTraffic);
	// const returningTraffic = useAppSelector(_returningTraffic);
	const topContent = useAppSelector(_topContent);
	const sessionData = useAppSelector(_sessionData);
	const viewsBy = useAppSelector(_viewsBy);
	const downloadsData = useAppSelector(_downloadsData);
	const trafficDaily = useAppSelector(_trafficDaily);
	const hourly = useAppSelector(_hourly);
	const languagesAccessed = useAppSelector(_languagesAccessed);
	const botRenderedShares = useAppSelector(_botRenderedShares);
	const buttonClicks = useAppSelector(_buttonClicks);
	const qrCodeScans = useAppSelector(_qrCodeScans);

	return (
		<Container className={classes.containerWidth}>
			<Keyframes name="fade" _0={{ opacity: 0 }} _100={{ opacity: 1 }} />
			{/* MAIN GRID CONTAINER */}
			<Grid container spacing={1} direction="row" justifyContent="flex-start" alignItems="stretch">
				{/* page title */}
				<Grid item xs={4}>
					<Typography variant="h5" className={classes.dashboardTitle}>
						Analytics
					</Typography>
					{/* breadcrumbs */}
					<Breadcrumbs aria-label="breadcrumb">
						{/* app dashboard */}
						<Typography className={classes.breadcrumbLink} onClick={toDashboard}>
							<HomeIcon fontSize="inherit" />
							Dashboard
						</Typography>
						{/* analytics */}
						<Typography>Analytics</Typography>
					</Breadcrumbs>
				</Grid>

				{/* empty */}
				<Grid item xs={4}></Grid>

				{/* date range selector */}
				<Grid item xs={4}>
					<ViiisionDateRangePicker />
				</Grid>

				{/* START ANALYTICS CARDS */}
				<Grid item xs={8}>
					<Grid container>
						{/* Page Views - All Topics */}
						<Grid item xs={6}>
							<Card elevation={1} className={classes.card}>
								{isLoading ? (
									<Skeleton variant="text" width="100%" height={540} className={classes.fade} />
								) : (
									<div
										className={classes.fade}
										style={{
											width: '100%',
											height: '540px',
											overflow: 'auto',
											padding: '0px 10px',
										}}
									>
										<TopicViewsCount data={pageViews} topics={topics} />
									</div>
								)}
							</Card>
						</Grid>
						{/* row 1, 2 cards */}
						<Grid xs={6}>
							<Grid container>
								<Grid item xs={12}>
									<Card elevation={1} className={classes.card}>
										{isLoading ? (
											<Skeleton
												variant="text"
												width="100%"
												height={210}
												className={classes.fade}
											/>
										) : (
											<div className={classes.fade} style={{ width: '100%', height: '210px' }}>
												<PageViews data={pageViews} topics={topics} />
											</div>
										)}
									</Card>
								</Grid>

								<Grid item xs={12}>
									<Card elevation={1} className={classes.card}>
										{isLoading ? (
											<Skeleton
												variant="text"
												width="100%"
												height={210}
												className={classes.fade}
											/>
										) : (
											<div className={classes.fade} style={{ width: '100%', height: '210px' }}>
												<Traffic
													userTraffic={userTraffic}
													// newTraffic={newTraffic}
													// returningTraffic={returningTraffic}
												/>
											</div>
										)}
									</Card>
								</Grid>
							</Grid>
						</Grid>
						{/* row 2, one card */}
						<Grid item xs={12}>
							<Card elevation={1} className={classes.card}>
								{isLoading ? (
									<Skeleton variant="text" width="100%" height={240} className={classes.fade} />
								) : (
									<div className={classes.fade} style={{ width: '100%', height: '240px' }}>
										<ViewsBy data={viewsBy} />
									</div>
								)}
							</Card>
						</Grid>
					</Grid>
				</Grid>
				{/* END LEFT SIDE */}

				{/* RIGHT SIDE, 1 card spanning 2 rows */}
				<Grid item xs={4}>
					<Grid container direction="column">
						<Grid item xs={12}>
							<Card elevation={1} className={classes.card}>
								{isLoading ? (
									<Skeleton variant="text" width="100%" height={530} className={classes.fade} />
								) : (
									<div className={classes.fade} style={{ width: '100%', height: '530px' }}>
										<MostViewed data={topContent} topics={topics} />
									</div>
								)}
							</Card>
						</Grid>

						{/*  */}
						<Grid item xs={12}>
							<Card elevation={1} className={classes.card}>
								{isLoading ? (
									<Skeleton variant="text" width="100%" height={213} className={classes.fade} />
								) : (
									<div className={classes.fade} style={{ width: '100%', height: 'auto' }}>
										<Downloads downloads={downloadsData} />
									</div>
								)}
							</Card>
						</Grid>
					</Grid>
				</Grid>
				{/* END RIGHT SIDE */}

				<Grid item xs={6}>
					<Card elevation={1} className={classes.card}>
						{isLoading ? (
							<Skeleton variant="text" width={446} height={265} className={classes.fade} />
						) : (
							<div className={classes.fade} style={{ width: '100%', height: 'auto' }}>
								<UserCard session={sessionData.session} sessionDuration={sessionData.sessionDuration} />
							</div>
						)}
					</Card>
				</Grid>
				{/* END THIRD ROW */}

				{/* FOURTH ROW, full width, 2 cards wide */}
				<Grid item xs={6}>
					<Card elevation={1} className={classes.card}>
						{isLoading ? (
							<Skeleton variant="text" width={446} height={200} className={classes.fade} />
						) : (
							<div className={classes.fade} style={{ width: '100%', height: 'auto' }}>
								<TrafficDaily data={trafficDaily} />
							</div>
						)}
					</Card>
				</Grid>

				<Grid item xs={6}>
					<Card elevation={1} className={classes.card}>
						{isLoading ? (
							<Skeleton variant="text" width="446px" height={250} className={classes.fade} />
						) : (
							<div className={classes.fade} style={{ width: '100%', height: '300px' }}>
								<Hourly hourly={hourly} />
							</div>
						)}
					</Card>
				</Grid>
				{/* END FOURTH ROW */}
				<Grid item xs={6}>
					<Card elevation={1} className={classes.card}>
						{isLoading ? (
							<Skeleton variant="text" width="100%" height={250} className={classes.fade} />
						) : (
							<div className={classes.fade} style={{ width: 'auto', height: '300px' }}>
								<BotRenderedShares data={botRenderedShares} topics={topics} />
							</div>
						)}
					</Card>
				</Grid>

				<Grid item xs={6}>
					<Card elevation={1} className={classes.card}>
						{isLoading ? (
							<Skeleton variant="text" width="100%" height={250} className={classes.fade} />
						) : (
							<div className={classes.fade} style={{ width: 'auto', height: '300px' }}>
								<ButtonClicks data={buttonClicks} />
							</div>
						)}
					</Card>
				</Grid>

				{/* Button Clicks Table Section */}
				<Grid item xs={6}>
					<Card elevation={1} className={classes.card}>
						{isLoading ? (
							<Skeleton variant="text" width="100%" height={250} className={classes.fade} />
						) : (
							<div className={classes.fade} style={{ width: '100%', height: '300px' }}>
								<ButtonClicksTable buttonClickData={buttonClicks} topics={topics} />
							</div>
						)}
					</Card>
				</Grid>

				{/* QR Code Scans - Scan By Type */}
				<Grid item xs={6}>
					<Card elevation={1} className={classes.card}>
						{isLoading ? (
							<Skeleton variant="text" width="100%" height={250} className={classes.fade} />
						) : (
							<div className={classes.fade} style={{ width: '100%', height: '300px' }}>
								<ScanByDate data={qrCodeScans} />
							</div>
						)}
					</Card>
				</Grid>

				{/* QR Code Scans - Scan By Code Table */}
				<Grid item xs={6}>
					<Card elevation={1} className={classes.card}>
						{isLoading ? (
							<Skeleton variant="text" width="100%" height={250} className={classes.fade} />
						) : (
							<div className={classes.fade} style={{ width: '100%', height: '300px' }}>
								<ScanByCode qrCodeScanData={qrCodeScans} topics={topics} />
							</div>
						)}
					</Card>
				</Grid>

				{/*Start of Fifth Row 2 cards wide */}
				<Grid item xs={12}>
					<Card elevation={1} className={classes.card}>
						{isLoading ? (
							<Skeleton variant="text" width="100%" height={350} className={classes.fade} />
						) : (
							<div className={classes.fade} style={{ width: '100%', height: '350px' }}>
								<LanguagesAccessedCard
									languageData={languagesAccessed}
									totalResponses={languagesAccessed.length}
								/>
							</div>
						)}
					</Card>
				</Grid>
				{/*End of Fifth Row */}

				{/* // grid just to create a bottom empty footer space so the tooltip doesn;t cover the last card */}
				<Grid item xs={12}>
					{/* // span with opaque font color and tiny font size to create a bottom footer space */}
					<span style={{ color: 'rgba(0, 0, 0, .00)', fontSize: '.1rem' }}>
						Hello! I'm a footer space!
						<br />
						This text is hidden. I'm just putting it here to add some space.
						<br />
						But you're not supposed to see it, so I made the font color invisible.
						<br />
					</span>
				</Grid>
			</Grid>
			<AnalyticsTip />
		</Container>
	);
};
